import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  getDocs,
  onSnapshot,
  serverTimestamp,
  Unsubscribe,
  DocumentData,
  QueryDocumentSnapshot,
  writeBatch
} from 'firebase/firestore'
import { User } from 'firebase/auth'
import { db } from '@/lib/firebase'
import {
  Vehicle,
  CreateVehicleData,
  UpdateVehicleData,
  VehicleValidationError,
  VehicleError
} from '@/types/vehicle'

// Validation utilities
export class VehicleValidator {
  static validateVehicle(data: CreateVehicleData | UpdateVehicleData): VehicleValidationError[] {
    const errors: VehicleValidationError[] = []

    // Validate carRegNumber if provided
    if ('carRegNumber' in data) {
      if (!data.carRegNumber || data.carRegNumber.trim().length === 0) {
        errors.push({ field: 'carRegNumber', message: 'Registreringsnummer är obligatoriskt' })
      } else if (data.carRegNumber.trim().length < 2) {
        errors.push({ field: 'carRegNumber', message: 'Registreringsnummer måste vara minst 2 tecken' })
      }
    }

    return errors
  }
}

// Main Vehicle Service
export class VehicleService {
  private static readonly COLLECTION_NAME = 'vehicles'

  // Get collection reference for a user
  private static getUserCollection(user: User) {
    return collection(db, 'users', user.uid, this.COLLECTION_NAME)
  }

  // Get document reference for a specific vehicle
  private static getVehicleDocument(user: User, vehicleId: string) {
    return doc(db, 'users', user.uid, this.COLLECTION_NAME, vehicleId)
  }

  // Convert Firestore document to Vehicle with proper typing
  private static convertFirestoreDoc(doc: QueryDocumentSnapshot<DocumentData>): Vehicle {
    const data = doc.data()
    return {
      id: doc.id,
      carRegNumber: data.carRegNumber || '',
      isActive: data.isActive !== false, // Default to true if not set
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
    }
  }

  // Get all active vehicles
  static async getVehicles(user: User): Promise<Vehicle[]> {
    try {
      const q = query(
        this.getUserCollection(user),
        where('isActive', '==', true),
        orderBy('carRegNumber', 'asc')
      )

      const querySnapshot = await getDocs(q)
      const vehicles = querySnapshot.docs.map(doc => this.convertFirestoreDoc(doc))

      return vehicles
    } catch (error) {
      console.error('Error fetching vehicles:', error)
      throw new Error('Det gick inte att hämta fordon')
    }
  }

  // Get single vehicle by ID (includes inactive vehicles)
  static async getVehicle(user: User, id: string): Promise<Vehicle | null> {
    try {
      const docRef = this.getVehicleDocument(user, id)
      const docSnap = await getDoc(docRef)

      if (!docSnap.exists()) {
        return null
      }

      const vehicle = this.convertFirestoreDoc(docSnap)
      return vehicle
    } catch (error) {
      console.error('Error fetching vehicle:', error)
      throw new Error('Det gick inte att hämta fordonet')
    }
  }

  // Get single active vehicle by ID (filters by isActive=true)
  static async getActiveVehicle(user: User, id: string): Promise<Vehicle | null> {
    const vehicle = await this.getVehicle(user, id)
    return vehicle?.isActive ? vehicle : null
  }

  // Create new vehicle
  static async createVehicle(user: User, data: CreateVehicleData): Promise<Vehicle> {
    try {
      // Validate input
      const errors = VehicleValidator.validateVehicle(data)
      if (errors.length > 0) {
        throw new Error(errors[0].message)
      }

      // Check for duplicate registration number
      const existingVehicles = await this.getVehicles(user)
      const duplicateRegNumber = existingVehicles.find(
        v => v.carRegNumber.toLowerCase() === data.carRegNumber.trim().toLowerCase()
      )
      if (duplicateRegNumber) {
        throw new Error('Ett fordon med detta registreringsnummer finns redan')
      }

      // Create new vehicle document with auto-generated ID
      const docRef = doc(this.getUserCollection(user))
      const now = serverTimestamp()

      const vehicleData = {
        carRegNumber: data.carRegNumber.trim(),
        isActive: true,
        createdAt: now,
        updatedAt: now,
      }

      await setDoc(docRef, vehicleData)

      // Get the created vehicle
      const createdVehicle = await this.getVehicle(user, docRef.id)
      if (!createdVehicle) {
        throw new Error('Det gick inte att skapa fordonet')
      }

      return createdVehicle
    } catch (error) {
      console.error('Error creating vehicle:', error)
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Det gick inte att skapa fordonet')
    }
  }

  // Update existing vehicle
  static async editVehicle(user: User, id: string, updates: UpdateVehicleData): Promise<Vehicle> {
    try {
      // Validate input
      const errors = VehicleValidator.validateVehicle(updates)
      if (errors.length > 0) {
        throw new Error(errors[0].message)
      }

      const docRef = this.getVehicleDocument(user, id)

      // Check if vehicle exists
      const existingDoc = await getDoc(docRef)
      if (!existingDoc.exists()) {
        throw new Error('Fordonet hittades inte')
      }

      // Check for duplicate registration number if updating carRegNumber
      if (updates.carRegNumber) {
        const existingVehicles = await this.getVehicles(user)
        const duplicateRegNumber = existingVehicles.find(
          v => v.id !== id && v.carRegNumber.toLowerCase() === updates.carRegNumber!.trim().toLowerCase()
        )
        if (duplicateRegNumber) {
          throw new Error('Ett fordon med detta registreringsnummer finns redan')
        }
      }

      // Prepare update data
      const updateData: { [key: string]: any } = {
        updatedAt: serverTimestamp()
      }

      if (updates.carRegNumber !== undefined) {
        updateData.carRegNumber = updates.carRegNumber.trim()
      }

      await updateDoc(docRef, updateData)

      // Get updated vehicle
      const updatedVehicle = await this.getVehicle(user, id)
      if (!updatedVehicle) {
        throw new Error('Det gick inte att uppdatera fordonet')
      }

      return updatedVehicle
    } catch (error) {
      console.error('Error updating vehicle:', error)
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Det gick inte att uppdatera fordonet')
    }
  }

  // Soft delete vehicle
  static async deleteVehicle(user: User, id: string): Promise<void> {
    try {
      const docRef = this.getVehicleDocument(user, id)

      // Check if vehicle exists
      const existingDoc = await getDoc(docRef)
      if (!existingDoc.exists()) {
        throw new Error('Fordonet hittades inte')
      }

      await updateDoc(docRef, {
        isActive: false,
        updatedAt: serverTimestamp()
      })

    } catch (error) {
      console.error('Error deleting vehicle:', error)
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Det gick inte att ta bort fordonet')
    }
  }

  // Subscribe to vehicles changes
  static subscribeToVehicles(user: User, callback: (vehicles: Vehicle[]) => void): Unsubscribe {
    try {
      const q = query(
        this.getUserCollection(user),
        where('isActive', '==', true),
        orderBy('carRegNumber', 'asc')
      )

      return onSnapshot(q, (querySnapshot) => {
        const vehicles = querySnapshot.docs.map(doc => this.convertFirestoreDoc(doc))
        callback(vehicles)
      }, (error) => {
        console.error('Error in vehicles subscription:', error)
        callback([])
      })
    } catch (error) {
      console.error('Error setting up vehicles subscription:', error)
      return () => {} // Return empty unsubscribe function
    }
  }
}